// Detector output → page geometry, and the rule for what counts as a
// duplicate frame.

const BOX_SCALE = 1000; // Qwen3-VL grounding boxes are normalised to [0..1000]

/**
 * Map one `bbox_2d` onto page coordinates.
 *
 * The detector sees a downscaled image but reports normalised coordinates,
 * so the downscale factor cancels out and only the page's own dimensions
 * matter.
 */
export function boxToPageClip(bbox2d, { pageWidth, pageHeight, drawingScale = null }) {
  if (!Array.isArray(bbox2d) || bbox2d.length !== 4) return null;
  if (bbox2d.some((n) => typeof n !== "number" || Number.isNaN(n))) return null;

  const [x0, y0, x1, y1] = bbox2d;

  const left = (Math.min(x0, x1) / BOX_SCALE) * pageWidth;
  const right = (Math.max(x0, x1) / BOX_SCALE) * pageWidth;
  const top = (Math.min(y0, y1) / BOX_SCALE) * pageHeight;
  const bottom = (Math.max(y0, y1) / BOX_SCALE) * pageHeight;

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
    drawing_scale: drawingScale,
    drawing_offset: { x: 0, y: 0 },
  };
}

/** Intersection over union of two clip rectangles. */
export function iou(a, b) {
  const left = Math.max(a.x, b.x);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const top = Math.max(a.y, b.y);
  const bottom = Math.min(a.y + a.height, b.y + b.height);

  const overlapW = right - left;
  const overlapH = bottom - top;
  if (overlapW <= 0 || overlapH <= 0) return 0;

  const intersection = overlapW * overlapH;
  const union = a.width * a.height + b.width * b.height - intersection;
  return union <= 0 ? 0 : intersection / union;
}

/**
 * Idempotency rule for the locator: re-running on a sheet must add only what
 * is new. Matching is geometric because detector output carries no stable
 * identity between runs.
 *
 * 0.5 tolerates small coordinate drift between runs while still treating two
 * genuinely adjacent frames as distinct. Expect to tune it against real
 * sheets.
 */
export function dedupeAgainstExisting(candidates, existingClips, threshold = 0.5) {
  const kept = [];
  const seen = [...(existingClips || [])];

  (candidates || []).forEach((candidate) => {
    if (!candidate) return;
    const duplicate = seen.some((existing) => iou(candidate, existing) >= threshold);
    if (duplicate) return;
    kept.push(candidate);
    seen.push(candidate);
  });

  return kept;
}
